import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { useApplyFolderTemplate } from '../../hooks/queries/useDossierFiles';
import type { FolderTemplateSlug } from '../../lib/folderTemplates';
import { Button } from '../ui/Button';
import { FolderTemplatePicker } from './FolderTemplatePicker';

interface Props {
  dossierId: string;
  onClose: () => void;
}

export function ApplyFolderTemplateModal({ dossierId, onClose }: Props) {
  const [choice, setChoice] = useState<FolderTemplateSlug>('conseil');
  const apply = useApplyFolderTemplate(dossierId);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const result = await apply.mutateAsync(choice);
      if (result.skipped) {
        toast.message('Ce dossier a déjà des sous-dossiers — arborescence non appliquée.');
      } else {
        toast.success(`${result.created} sous-dossiers créés.`);
      }
      onClose();
    } catch {
      /* affiché via apply.isError */
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-md shadow-xl w-full max-w-lg"
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
          <h2 className="font-heading text-lg font-semibold">Appliquer une arborescence</h2>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-5 py-4 space-y-3">
          <FolderTemplatePicker
            value={choice}
            onChange={setChoice}
            includeNone={false}
          />
          {apply.isError && (
            <p className="text-xs text-red-600">
              {apply.error instanceof Error ? apply.error.message : 'Erreur'}
            </p>
          )}
        </div>
        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <Button variant="ghost" type="button" onClick={onClose} disabled={apply.isPending}>
            Annuler
          </Button>
          <Button type="submit" disabled={apply.isPending}>
            {apply.isPending ? 'Application…' : 'Appliquer'}
          </Button>
        </footer>
      </form>
    </div>
  );
}
